import { Icon, TextLink } from "@components/library";
import { FullWidthDivider } from "@components/library/Divider";
import RfpCard from "@components/RfpCard/RfpCard";
import { COLORS, FONTS, WINDOW_DIMENSIONS } from "@constants";
import { RootState } from "@redux/store";
import { getMarketplaceRequests } from "@requests/requests";
import { marketplacePath } from "@routes/utils";
import { useQuery } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import appsignal from "@utils/appsignal";
import { track } from "@utils/appUtils";
import { t } from "@utils/i18n";
import { sortMarketplaceRequests } from "@utils/requestUtils";
import { useSelector } from "react-redux";
import { SortBy } from "src/views/Marketplace/RespondToRequests";
import styled from "styled-components";

const i18nPrefix = "components.recommended_partnering_requests";
const MAX_RECOMMENDED_REQUESTS = 4;

interface Props {
  requestId: number;
  className?: string;
}

function getNotRelevantRequestsFromLocalStorage() {
  return Object.entries(localStorage)
    .filter(([key]) => key.startsWith("request_preference_"))
    .map(([key, value]) => {
      if (value && JSON.parse(value) === false) {
        return Number(key.replace("request_preference_", ""));
      }
      return null;
    })
    .filter((id): id is number => id !== null);
}

const RecommendedPartneringRequests = ({ requestId, className }: Props) => {
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const isLoggedIn = currentUser.isLoggedIn;

  const { data, isLoading } = useQuery(
    ["getMarketplaceRequests"],
    () =>
      getMarketplaceRequests({
        searchFilter: [],
        useCaseFilter: [],
        partnershipModelsFilter: [],
        partnerTypesFilter: [],
      }),
    {
      onError: (error) => {
        appsignal.sendError(error as Error, (span) => {
          span.setAction("RecommendedPartneringRequests#getMarketplaceRequests");
        });
      },
    }
  );

  const requests = data?.requests ?? [];
  const requestScores = data?.scores ?? {};

  sortMarketplaceRequests({ requests, scores: requestScores, sort: SortBy.RELEVANCE });

  const notRelevantRequests = getNotRelevantRequestsFromLocalStorage();

  const recommendedRequests = requests
    .filter((request) => request.id !== requestId)
    .filter((request) => isLoggedIn || !notRelevantRequests.includes(request.id))
    .slice(0, MAX_RECOMMENDED_REQUESTS);

  if (isLoading) return null;

  return (
    <>
      <FullWidthDivider />
      <Container className={className}>
        <Header>
          <TitleSection>
            <Icon name="RFP" size="md" />
            <Title>{t([i18nPrefix, "title"])}</Title>
          </TitleSection>
          <TextLink to={marketplacePath("requests")} color={COLORS.BLUE_500}>
            {t([i18nPrefix, "see_more"])}
            <Icon name="Arrow Right" margin="0 -4px 0 4px" color={COLORS.BLUE_500} size="sm" />
          </TextLink>
        </Header>
        <CardsContainer>
          {recommendedRequests.map((request) => (
            <RfpCard
              key={request.id}
              request={request}
              onClick={() => {
                track(SegmentEventName.CLICK, {
                  react_component: "RecommendedPartneringRequests",
                  request_slug: request.slug,
                });
              }}
            />
          ))}
        </CardsContainer>
      </Container>
    </>
  );
};

export default RecommendedPartneringRequests;

const Container = styled.div`
  position: relative;
  padding-top: 54px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-bottom: 52px;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Title = styled.h3`
  ${FONTS.SEMIBOLD_1};
  margin: 0;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  justify-content: center;
  ${WINDOW_DIMENSIONS.DESKTOP_MEDIA_QUERY} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY} {
    grid-template-columns: 1fr;
  }
`;
